import { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Gauge, KeyRound, ShieldAlert, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/States";
import { POLICIES, POLICY_VERSION } from "@/lib/policies";

const ACTION_LABEL = (a) => a.replace(/[._]/g, " ");
const when = (iso) => new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function MfaEnrol({ onDone, onCancel }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const start = () => run(async () => { const { data } = await api.post("/auth/mfa/enrol/start"); setSetup({ ...data, t: Date.now() }); });
  const confirm = () => run(async () => { const { data } = await api.post("/auth/mfa/enrol/confirm", { code }); setBackup(data.backup_codes); setSetup(null); setCode(""); toast.success("Two-step sign-in is on"); });
  if (backup) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm" data-testid="mfa-backup-codes">
        <p className="flex items-center gap-2 font-medium"><KeyRound className="h-4 w-4" />Save these backup codes now — they are shown once.</p>
        <p className="mt-1 text-xs text-muted-foreground">Each works one time if you lose your phone. Keep them somewhere safe, not in email.</p>
        <pre className="mt-3 grid select-all grid-cols-2 gap-x-6 font-mono text-sm sm:grid-cols-4">{backup.map((c) => <span key={c}>{c}</span>)}</pre>
        <Button size="sm" variant="outline" className="mt-3 rounded-full" onClick={() => { setBackup(null); onDone?.(); }} data-testid="mfa-backup-done">I've saved them</Button>
      </div>
    );
  }
  if (!setup) return <Button className="rounded-full" onClick={start} disabled={busy} data-testid="mfa-start">{busy && <Spinner />} Turn on</Button>;
  return (
    <div className="grid gap-4 sm:grid-cols-[180px_1fr]" data-testid="mfa-setup">
      <img src={fileUrl(`/api/auth/mfa/enrol/qr.png?t=${setup.t}`)} alt="Scan with your authenticator app" className="h-[180px] w-[180px] rounded-md border bg-white p-1" data-testid="mfa-qr" />
      <div className="space-y-3 text-sm">
        <p>1. Scan the code with your authenticator app (Google Authenticator, 1Password, Authy), or enter this key by hand:</p>
        <code className="block select-all break-all rounded bg-secondary px-2 py-1 font-mono text-xs" data-testid="mfa-secret">{setup.secret}</code>
        <p>2. Enter the six-digit code it shows.</p>
        <div className="flex flex-wrap gap-2">
          <Input inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="w-32 font-mono" data-testid="mfa-code-input" />
          <Button onClick={confirm} disabled={code.length !== 6 || busy} data-testid="mfa-confirm">{busy ? <Spinner /> : "Confirm"}</Button>
          {onCancel !== null && <Button variant="ghost" onClick={() => { setSetup(null); onCancel?.(); }} data-testid="mfa-cancel">Cancel</Button>}
        </div>
      </div>
    </div>
  );
}

export function MfaCard() {
  const status = useFetch("/auth/mfa/status");
  const { refreshUser } = useAuth();
  const [disable, setDisable] = useState(null);
  const [busy, setBusy] = useState(false);
  const off = async () => {
    setBusy(true);
    try { await api.post("/auth/mfa/disable", disable); setDisable(null); status.reload(); refreshUser(); toast.message("Two-step sign-in turned off"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const on = status.data?.enabled;
  return (
    <section className="rounded-xl border bg-card p-5" data-testid="mfa-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-semibold">{on ? <ShieldCheck className="h-4 w-4 text-primary" /> : <ShieldOff className="h-4 w-4 text-muted-foreground" />}Two-step sign-in</h3>
          <p className="mt-1 text-sm text-muted-foreground">{on ? `On since ${status.data.enabled_at?.slice(0, 10)} · ${status.data.backup_codes_left} backup codes left` : "Add a six-digit code from an authenticator app to your password."}</p>
        </div>
        {on && !disable && <Button variant="outline" className="rounded-full" onClick={() => setDisable({ password: "", code: "" })} data-testid="mfa-disable-open">Turn off</Button>}
      </div>
      {status.data && !on && <div className="mt-5"><MfaEnrol onDone={() => { status.reload(); refreshUser(); }} /></div>}
      {disable && (
        <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_140px_auto_auto] sm:items-end" data-testid="mfa-disable-form">
          <div className="space-y-1"><Label htmlFor="mfa-pw">Your password</Label><Input id="mfa-pw" type="password" value={disable.password} onChange={(e) => setDisable({ ...disable, password: e.target.value })} data-testid="mfa-disable-password" /></div>
          <div className="space-y-1"><Label htmlFor="mfa-dc">Current code</Label><Input id="mfa-dc" inputMode="numeric" maxLength={6} value={disable.code} onChange={(e) => setDisable({ ...disable, code: e.target.value.replace(/\D/g, "") })} className="font-mono" data-testid="mfa-disable-code" /></div>
          <Button variant="destructive" onClick={off} disabled={busy || !disable.password || disable.code.length !== 6} data-testid="mfa-disable-confirm">Turn off</Button>
          <Button variant="ghost" onClick={() => setDisable(null)}>Cancel</Button>
        </div>
      )}
    </section>
  );
}

export function RequireMfaCard() {
  const { data, reload } = useFetch("/workspace/security");
  const { user, refreshWorkspace } = useAuth();
  const [busy, setBusy] = useState(false);
  const toggle = async (v) => {
    setBusy(true);
    try { await api.put("/workspace/security", { require_admin_mfa: v }); await reload(); await refreshWorkspace(); toast.success(v ? "Two-step sign-in is now required for every admin" : "Two-step sign-in is optional for admins again"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const on = !!data?.require_admin_mfa;
  const without = (data?.admins || []).filter((a) => !a.mfa_enabled);
  return (
    <section className="rounded-xl border bg-card p-5" data-testid="require-mfa-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-semibold"><ShieldAlert className={`h-4 w-4 ${on ? "text-primary" : "text-muted-foreground"}`} />Require two-step for admins</h3>
          <p className="mt-1 text-sm text-muted-foreground">When on, every admin must set up two-step sign-in the next time they sign in — nothing else opens until they do. Crew and clients are not affected.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="require-mfa" className="text-sm">{on ? "Required" : "Optional"}</Label>
          <Switch id="require-mfa" checked={on} disabled={busy || !data || (!on && !user.mfa_enabled)} onCheckedChange={toggle} data-testid="require-mfa-switch" />
        </div>
      </div>
      {data && !on && !user.mfa_enabled && <p className="mt-3 text-xs text-amber-700" data-testid="require-mfa-hint">Turn on two-step sign-in for your own account first.</p>}
      {data && (
        <ul className="mt-4 divide-y text-sm" data-testid="admin-mfa-list">
          {data.admins.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 py-1.5" data-testid={`admin-mfa-${a.id}`}>
              <span className="truncate">{a.name || a.email}<span className="ml-2 text-xs text-muted-foreground">{a.email}</span></span>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] ${a.mfa_enabled ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}>{a.mfa_enabled ? "Two-step on" : on ? "Will set up at next sign-in" : "Not set up"}</span>
            </li>
          ))}
        </ul>
      )}
      {on && without.length > 0 && <p className="mt-2 text-xs text-muted-foreground">{without.length} admin{without.length > 1 ? "s" : ""} still to set up.</p>}
    </section>
  );
}

export function AiUsageCard() {
  const { data } = useFetch("/workspace/ai-usage");
  return (
    <section className="rounded-xl border bg-card p-5" data-testid="ai-usage-card">
      <h3 className="flex items-center gap-2 font-semibold"><Gauge className="h-4 w-4 text-muted-foreground" />Today's AI allowance</h3>
      <p className="mt-1 text-sm text-muted-foreground">A fair daily allowance per workspace keeps costs predictable{data ? ` — ${data.trial ? "trial" : `${data.plan} plan`} limits, reset at midnight Toronto time` : ""}.</p>
      {!data ? <Spinner /> : (
        <ul className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="ai-usage-list">
          {data.items.map((it) => {
            const pct = Math.min(100, Math.round((it.used / it.limit) * 100));
            return (
              <li key={it.kind} className="rounded-lg border p-3" data-testid={`ai-usage-${it.kind}`}>
                <p className="text-xs capitalize text-muted-foreground">{it.label}</p>
                <p className="stat mt-1 text-xl"><span data-testid={`ai-usage-${it.kind}-used`}>{it.used}</span><span className="text-sm text-muted-foreground"> / {it.limit}</span></p>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary"><div className={`h-full rounded-full ${pct >= 100 ? "bg-destructive" : "bg-primary"}`} style={{ width: `${pct}%` }} /></div>
                {pct >= 100 && <p className="mt-1.5 text-[11px] text-destructive" data-testid={`ai-usage-${it.kind}-reached`}>Allowance reached — resets at midnight</p>}
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-3 text-xs text-muted-foreground">AI assists cover the Board co-designer and critique, image reimagining, room renders, narration audio and daily-log tidy-up.</p>
    </section>
  );
}

export function PoliciesCard() {
  return (
    <section className="rounded-xl border bg-card p-5" data-testid="policies-card">
      <h3 className="flex items-center gap-2 font-semibold"><FileText className="h-4 w-4 text-muted-foreground" />Security policies</h3>
      <p className="mt-1 text-sm text-muted-foreground">Four one-page E.L.M platform policies ({POLICY_VERSION}). Open, read or print them for your own records or a client's due-diligence request.</p>
      <ul className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="policies-list">
        {POLICIES.map((p) => (
          <li key={p.slug}>
            <Link to={`/workspace/policies/${p.slug}`} className="block rounded-lg border p-3 transition-colors hover:bg-secondary/60" data-testid={`policy-open-${p.slug}`}>
              <p className="text-sm font-medium">{p.title}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{p.summary}</p>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function AuditLog() {
  const { data, loading } = useFetch("/audit?limit=100");
  return (
    <section className="rounded-xl border bg-card p-5" data-testid="audit-log">
      <h3 className="font-semibold">Activity log</h3>
      <p className="mt-1 text-sm text-muted-foreground">Sign-ins, invites, exports, settings changes, approvals and other sensitive actions. Kept for 400 days.</p>
      {loading ? <Spinner /> : !data?.length ? <p className="mt-4 text-sm text-muted-foreground" data-testid="audit-empty">Nothing logged yet.</p> : (
        <ul className="mt-4 divide-y text-sm" data-testid="audit-list">
          {data.map((e) => (
            <li key={e.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2" data-testid={`audit-${e.id}`}>
              <span className="w-32 shrink-0 text-xs text-muted-foreground tabular-nums">{when(e.created_at)}</span>
              <span className="font-medium capitalize">{ACTION_LABEL(e.action)}</span>
              <span className="text-muted-foreground">{e.user_email}{e.target ? ` · ${e.meta?.name || e.target}` : ""}{e.ip ? ` · ${e.ip}` : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
